<script setup lang="ts">
/**
 * The front page's settings.
 *
 * The arrangement comes first, and it is chosen from thumbnails rather than a
 * list of names. "Sidebar" and "Band" mean nothing until you have seen them,
 * and a person choosing a cover is making a visual decision: the picker should
 * be visual too.
 *
 * The image fields follow the arrangement rather than sitting there always. An
 * arrangement with nowhere to put a photograph should not offer one, because
 * the offer implies it would appear somewhere.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { CoverSection } from '@/domain/section'
import CoverVariantPicker from '@/sections/cover/CoverVariantPicker.vue'
import { usesLogo, usesPicture, type CoverVariant } from '@/sections/cover/variants'
import ImagePicker from '@/sections/ImagePicker.vue'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: CoverSection }>()
const emit = defineEmits<{ change: [section: CoverSection] }>()

const takesPicture = computed(() => usesPicture(props.section.variant))
const takesLogo = computed(() => usesLogo(props.section.variant))

/** `full` is a picture and nothing else: the text fields would have nowhere
 *  to appear, so offering them would only invite typing into the void. */
const hasBlock = computed(() => props.section.variant !== 'full')

function set(changes: Partial<CoverSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <CoverVariantPicker
      :chosen="section.variant"
      @choose="set({ variant: $event as CoverVariant })"
    />

    <div v-if="takesLogo || takesPicture" class="images">
      <ImagePicker
        v-if="takesLogo"
        shape="mark"
        :source="section.logo"
        :label="t('inspector.cover.logo')"
        :hint="t('inspector.cover.logoHint')"
        @change="set({ logo: $event })"
      />

      <ImagePicker
        v-if="takesPicture"
        :source="section.picture"
        :label="t('inspector.cover.picture')"
        :hint="t('inspector.cover.pictureHint')"
        @change="set({ picture: $event })"
      />
    </div>

    <template v-if="hasBlock">
      <InspectorField :label="t('inspector.cover.entity')">
        <input
          class="b-input"
          :value="section.entity"
          :placeholder="t('inspector.cover.entityPlaceholder')"
          @input="set({ entity: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.cover.subtitle')">
        <input
          class="b-input"
          :value="section.subtitle"
          :placeholder="t('inspector.cover.subtitlePlaceholder')"
          @input="set({ subtitle: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.cover.period')">
        <input
          class="b-input"
          :value="section.period"
          :placeholder="t('inspector.cover.periodPlaceholder')"
          @input="set({ period: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField
        :label="t('inspector.cover.standard')"
        :hint="t('inspector.cover.standardHint')"
      >
        <input
          class="b-input"
          :value="section.standard"
          :placeholder="t('inspector.cover.standardPlaceholder')"
          @input="set({ standard: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.cover.note')" :hint="t('inspector.cover.noteHint')">
        <input
          class="b-input"
          :value="section.note"
          :placeholder="t('inspector.cover.notePlaceholder')"
          @input="set({ note: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </template>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.images {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}
</style>
